import React, { forwardRef, useEffect, useRef, useState } from 'react'
import {
	StyledPhotoEditorPhotoUploadContainer,
	StyledPhotoEditorPhotoUploadImg,
	StyledPhotoEditorWatermarkUploadImg,
} from 'components/Selfiebooth/PhotoEditor/styled'
import Sticker from 'components/Selfiebooth/PhotoEditor/sticker';
import { StickerSelfieBoothType, WatermarkSelfieBoothType } from '../../../types/selfiebooth'

interface PhotoUploadProps {
	photoUrl: string;
	stickers: StickerSelfieBoothType[];
	watermark: WatermarkSelfieBoothType;
	selectedStickerId: string|null;
	updateSticker: (id: string, updates: Partial<any>) => void;
	deleteSticker: (id: string) => void;
	selectSticker: (id: string) => void;
	imageRotation: number;
	setPhotoUrl: (img: string) => void;
}

const PhotoUpload = forwardRef<HTMLDivElement, PhotoUploadProps>(
	({ photoUrl, stickers, watermark, selectedStickerId, updateSticker, deleteSticker, selectSticker, imageRotation, setPhotoUrl }, ref) => {
		const [watermarkSize, setWatermarkSize] = useState<{width: number; height: number;}>({ width: 0, height: 0 });
		const [photoHeight, setPhotoHeight] = useState<number>(0);
		const [photoWidth, setPhotoWidth] = useState<number>(0);
		const initialPhoto = useRef(photoUrl)

		useEffect(() => {
			if (!watermark?.url) return;

			const img = new Image();
			img.src = watermark.url;
			img.onload = () => {
				setWatermarkSize({ width: img.naturalWidth, height: img.naturalHeight });
			};
		}, [watermark?.url]);

		useEffect(() => {
			if (!photoUrl) return;

			const img =  new Image();
			img.src = initialPhoto.current;
			img.crossOrigin = "anonymous";
			img.onload = () => {
				const { naturalWidth, naturalHeight } = img;

				const canvas = document.createElement("canvas");
				const ctx = canvas.getContext("2d");

				let targetWidth: number, targetHeight: number;
				if(imageRotation % 180 !== 0) {
					targetWidth = Math.round((400 * naturalWidth) / naturalHeight)
					targetHeight = 400;
					canvas.width = targetWidth;
					canvas.height = targetHeight;
					setPhotoHeight(targetWidth)
					setPhotoWidth(targetHeight)
				} else {
					targetWidth = 400;
					targetHeight = Math.round((400 * naturalHeight) / naturalWidth);
					canvas.width = targetWidth;
					canvas.height = targetHeight;
					setPhotoHeight(targetHeight)
					setPhotoWidth(targetWidth)
				}

				ctx.save();
				ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
				ctx.restore();

				const newPhotoUrl = canvas.toDataURL("image/jpeg");

				if (newPhotoUrl !== photoUrl) {
					setPhotoUrl(newPhotoUrl);
				}
			};
		}, [imageRotation]);

		return (
			<StyledPhotoEditorPhotoUploadContainer
				ref={ref}
				$imageRotation={imageRotation}
				$photoHeight={photoHeight}
				$photoWidth={photoWidth}
			>
				<StyledPhotoEditorPhotoUploadImg
					src={photoUrl}
					$imageRotation={imageRotation}
					$photoHeight={photoHeight}
					$photoWidth={photoWidth}
				/>

				{stickers.map((sticker) => (
					<Sticker
						key={sticker.id}
						sticker={sticker}
						onUpdate={updateSticker}
						onDelete={deleteSticker}
						onSelect={selectSticker}
						isSelected={sticker.id === selectedStickerId}
					/>
				))}

				{watermark.isUse && <StyledPhotoEditorWatermarkUploadImg
					src={watermark.url}
					$size={watermark.size}
					$width={watermarkSize.width}
					$height={watermarkSize.height}
					$position={watermark.position}
					$opacity={watermark.opacity}
				/>}
			</StyledPhotoEditorPhotoUploadContainer>
		);
	}
);

export default PhotoUpload;
